import useSWR from "swr";
import useSWRInfinite from "swr/infinite";

function fetcher(url: string) {
  return (
    fetch(url)
      // Reject if the response is not OK
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
  );
}

export const DEFAULT_POLL_INTERVAL = 10_000;

type UseDataOptions = {
  poll?: boolean;
  pollIntervalMs?: number;
  revalidateOnFocus?: boolean;
};

export default function useData<T>(
  path: string | (() => string | undefined | null),
  options?: UseDataOptions
) {
  const { data, error, mutate, isValidating } = useSWR<T>(path, fetcher, {
    refreshInterval: options?.poll
      ? options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL
      : 0,
    // NOTE(gj): as currently written, passing `options` w/o specifying
    // `revalidateOnFocus` defaults to `undefined`, which overwrites the useSWR
    // default of `true`.
    revalidateOnFocus: options?.revalidateOnFocus,
  });

  return {
    data,
    isLoading: !data && !error,
    refresh: mutate,
    error,
    isValidating,
  };
}

export function useDataPolling<T>(
  path: string | (() => string | undefined | null),
  options?: Omit<UseDataOptions, "poll">
) {
  // Default to true if not explicitly set in options.
  const revalidateOnFocus = options?.revalidateOnFocus ?? true;

  return useData<T>(path, { ...options, poll: true, revalidateOnFocus });
}

type UseDataInfiniteOptions = UseDataOptions & {
  revalidateFirstPage?: boolean;
};

export function useDataInfinite<T>(
  getKey: (pageIndex: number, previousPageData: T | null) => string | null,
  options?: UseDataInfiniteOptions
) {
  const poll = options?.poll ?? true;

  const { data, error, mutate, isValidating, size, setSize } =
    useSWRInfinite<T>(getKey, fetcher, {
      refreshInterval: poll
        ? options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL
        : 0,
      // NOTE(gj): as currently written, passing `options` w/o specifying
      // `revalidateFirstPage` defaults to `undefined`, which overwrites the
      // useSWRInfinite default of `true`.
      revalidateFirstPage: options?.revalidateFirstPage,
      // NOTE(gj): as currently written, passing `options` w/o specifying
      // `revalidateOnFocus` defaults to `undefined`, which overwrites the
      // useSWRInfinite default of `true`.
      revalidateOnFocus: options?.revalidateOnFocus,
    });

  return {
    data,
    isLoading: !data && !error,
    refresh: mutate,
    error,
    isValidating,
    size,
    setSize,
  };
}
